import React, { useState, useEffect } from 'react'
import { Navbar, Nav, Button } from 'rsuite'
import { Routes, Route, Link, useMatch } from 'react-router-dom'
import Icon from './components/Icon'
import axios from 'axios'

// assets
import Logo from './assets/wimedo-icon.png'

// redux
import { useSelector, useDispatch } from 'react-redux'
import { getYou, getAskedYou } from './redux/options/selectors' 
import { setOption } from './redux/options/actions' 
import { setData, resetData } from './redux/data/actions'
import { setContests } from './redux/contests/actions'
import { getContests, getSelectedContest } from './redux/contests/selectors'
import groupTypes from './redux/options/groups'

// styles
import 'rsuite/styles/index.less'
import './css/App.less' 

// pages
import HomePage from './pages/HomePage'
import GroupPage from './pages/GroupPage'
import CarPage from './pages/CarPage'
import KioskPage from './pages/KioskPage'
import { KIOSK_PATH } from './tools/Kiosk'

// components
import NavLink from './components/NavLink'
import OptionsDrawer from './components/OptionsDrawer'
import { Notifications } from './components/Notifications'
import { getLastId } from './redux/data/selectors'
import { apiUrl, pollDelayShort, pollDelayLong } from './config'

/**
 * Timeout of the next data retrieval
 * @type {number}
 */
let timeoutId = 0

/**
 * Highest lap id loaded; the next request asks for laps after it, 0 loads all laps
 * @type {number}
 */
let lastId = 0

/**
 * Contest to load, 0 for the newest
 * @type {number}
 */
let contestId = 0

/**
 * Ids of the contests in the contest list
 * @type {Array<number>}
 */
let knownContestIds = []

/**
 * Request laps from the server
 * @param {number} contest contest id, 0 for the newest
 * @returns {Promise} axios request
 */
const requestData = ( contest ) =>
{
  const params = new URLSearchParams()
  if ( contest ) params.set( 'contest', contest )
  if ( lastId ) params.set( 'after', lastId )

  return axios.get( apiUrl + '?' + params.toString() )
}

/**
 * Request the contests with laps from the server
 * @returns {Promise} axios request
 */
const requestContests = () => axios.get( apiUrl + '?contests' )

const App = () => {

  // state
  const [ showOptions, setShowOptions ] = useState( false )

  // redux
  const you = useSelector( getYou )
  const askedYou = useSelector( getAskedYou )
  const dispatch = useDispatch()
  const loadedId = useSelector( getLastId )
  const selectedContest = useSelector( getSelectedContest )
  const contests = useSelector( getContests )

  // the kiosk screen has no header and its own options instead of the preferences
  const kiosk = useMatch( KIOSK_PATH ) !== null

  // share state with the request functions, which run outside renders
  lastId = loadedId
  contestId = selectedContest
  knownContestIds = contests.map( contest => contest.id )

  /**
   * Plan the next data retrieval; an earlier contest gets no new laps, so it is checked rarely
   * @param {number} delay milliseconds to wait, the poll delay when negative
   * @returns {void}
   */
  const queueData = ( delay = -1 ) =>
  {
    if ( delay < 0 ) delay = contestId ? pollDelayLong : pollDelayShort
    // reset interval
    if ( timeoutId ) clearTimeout( timeoutId );
    // plan the next data retrieval
    timeoutId = setTimeout( () => { loadData() }, delay )
  }

  /**
   * Load the contest list into state
   * @returns {void}
   */
  const loadContests = () =>
  {
    requestContests()
      .then( response =>
      {
        // an API without a contest list answers with laps; ignore those
        const { request = '', data: list = [] } = response && response.data ? response.data : {}
        if ( request === 'contests' ) dispatch( setContests( list ) )
      } )
      .catch( error => console.error( error ) )
  }

  /**
   * Load new laps into state and plan the next retrieval
   * @returns {void}
   */
  const loadData = () =>
  {
    // laps arriving after another contest was chosen are ignored
    const requested = contestId
    requestData( requested )
      .then( response =>
      {
        if ( !response || !response.data || requested !== contestId ) return

        const { data: rows = [], contest = null } = response.data
        if ( rows && rows.length )
        {
          dispatch( setData( rows, contest ) )
        }

        // a new contest started; refresh the list
        if ( contest && !knownContestIds.includes( contest.id ) )
        {
          loadContests()
        }
      } )
      // ignore errors
      .catch( error => console.error( error ) )
      // always queue next request
      .then( () => queueData() )
  }

  /**
   * Show the preferences once, so a visitor can enter a registration number; not on the kiosk screen
   * @returns {void}
   */
  const showYouForm = () =>
  {
    if ( !you && !askedYou && !kiosk )
    {
      setShowOptions( true )
      dispatch( setOption( 'askedYou', true ) )
    }
  }

  // load the contest list and show the preferences after mounting
  useEffect( () =>
  {
    loadContests()
    showYouForm()
  }, [] )

  // load all laps of another contest
  useEffect( () =>
  {
    lastId = 0
    dispatch( resetData() )
    dispatch( setOption( groupTypes.FAST, {} ) )
    queueData( 0 )
  }, [ selectedContest ] )

  // load all laps again after the data was reset with the reset button
  useEffect( () =>
  {
    if ( loadedId ) return
    dispatch( setOption( groupTypes.FAST, {} ) )
    queueData( 0 )
  }, [ loadedId ] )

  // show / hide options panel
  const onShowOptions = () => setShowOptions( true )
  const onHideOptions = () => setShowOptions( false )

  return (
      <div className={ kiosk ? 'WimedoApp kiosk' : 'WimedoApp' }>
        
        { /* the kiosk screen has no header; it shows its own buttons */ }
        { !kiosk && (
          <Navbar className="navbar" appearance="inverse">
            <Link to="/" className="logo">
            <img src={ Logo } alt="Wimedo logo" key="logo"/>
            </Link>
            <Navbar.Brand>            
              <Nav defaultActiveKey="home">              
                <NavLink icon="home" href="/" eventKey="home">Home</NavLink>
                <NavLink icon="hashtag" href="/group" eventKey="group">Group</NavLink>
                <NavLink icon="car" href="/car" eventKey="car">Car</NavLink>
              </Nav>
            </Navbar.Brand>      

            <Button
              onClick={ onShowOptions } 
              className="you"
            ><Icon icon="avatar" className={ you ? 'active' : '' } /> { you ? you : '0000' }</Button>

          </Navbar>
        ) }

        <div className="main-content">
          <Routes>
            <Route exact={ true } path="/" element={ <HomePage/> } />
            <Route path="/group/:group?" element={ <GroupPage/> } />
            <Route path="/car/:car?" element={ <CarPage/> } />
            <Route path={ KIOSK_PATH } element={ <KioskPage/> } />
          </Routes>  
        </div>   
        
        { /* the kiosk screen is public: no notifications about your car */ }
        <Notifications personal={ !kiosk }/>

        { /* the kiosk screen opens its own options over its buttons */ }
        { !kiosk && <OptionsDrawer show={ showOptions } onClose={ onHideOptions } /> }

      </div>
  )
}

export default App
